// Pure helpers for hh-empty's archived-in-place variant (Donna + Baker
// 2026-07-30). "“X household” has only an archived member — who should
// this household belong to?" recommended "Open & recover identity", but
// the flow offered no un-archive action anywhere — a dead end (UX rule
// #5): "It asking me to un-archive or re-identify… I have no way of
// doing this." The two honest answers are now one click in the rail:
//
//   verify — "Un-archive the member": the backend restores every
//     archived member of the household (resolved server-side) and
//     points the household's primary at them when it had none. 60s
//     undo re-archives with the prior reason + reopens.
//   archive — "Genuinely closed — archive the household": the generic
//     archive verb; refused with a plain-language note when active
//     work (a policy, loan, open opportunity…) is still attached.
//
// Only the archived-member variant gets this block: hh-empty's other
// variants (member moved away → merge into the survivor / truly empty
// → archive) carry merge/archive recommendations and keep their
// existing one-click paths. The variants are told apart by the stored
// recommendation verb ("review" = archived member in place).
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface ArchivedMemberIssueLike {
  detectorSlug: string;
  recommendation: { verb: string; params?: Record<string, unknown> } | null;
}

export function isArchivedMemberIssue(
  issue: ArchivedMemberIssueLike,
): boolean {
  // 2026-10-02: verb "review" alone also matched the "Where should the
  // empty household and its work go?" variant, which has no archived
  // member, so the un-archive button it led with failed every time. Only
  // the archived-member variant carries has_active_work.
  return (
    issue.detectorSlug === "hh-empty" &&
    issue.recommendation?.verb === "review" &&
    issue.recommendation.params?.["has_active_work"] !== undefined
  );
}

// Grant's rule (2026-07-30): a shell left behind by archiving a contact
// should itself be archived when nothing active is attached. The
// detector sets recommendation.params.has_active_work; when there is NO
// active work, archiving the household is the recommended default (and
// un-archive is the "no, this person belongs here" alternative). When
// active work IS attached the archive is blocked, so un-archive leads.
export function archivedMemberPrefersArchive(
  issue: ArchivedMemberIssueLike,
): boolean {
  return issue.recommendation?.params?.["has_active_work"] === false;
}

export const ARCHIVED_MEMBER_UNARCHIVE_LABEL =
  "Un-archive the member — recover this household";

export function archivedMemberUnarchiveConsequence(): string {
  return "Brings the archived person back as a live member of this household — they reappear in lists, search, and the household view, and become the household's primary contact if it had none. Nothing else about the record changes. Reversible for 60 seconds (undo re-archives them with the original reason), and from the record's History any time after.";
}

export const ARCHIVED_MEMBER_CLOSED_LABEL =
  "Archive the household too — nobody belongs here";

export function archivedMemberClosedConsequence(
  prefersArchive: boolean,
): string {
  return prefersArchive
    ? "Archiving the member left this household as an empty shell, so it should be archived too — it's removed from every list and view, but the row and a version snapshot are kept, so it's fully restorable. Reversible for 60 seconds here, and any time after."
    : "Archives the household — removed from every list and view, but the row and a version snapshot are kept, so it's fully restorable. If anything active is still attached (a policy, loan, open opportunity…), the archive is refused with a note saying what to relink first.";
}
